/**
 * 多边形绘制状态 Store。
 * 仅管理多边形绘制过程中的实时参数流。
 */

import { defineStore } from 'pinia';
import { PolygonDrawingEventType, type PolygonParameters } from '@puppycad/cad-core';
import { BehaviorSubject, type Observable, type Subscription } from 'rxjs';
import { useViewer } from '../useViewer';

/**
 * 多边形参数快照。
 */
export type PolygonParametersSnapshot = PolygonParameters | null;

/**
 * 多边形绘制进行中状态快照。
 */
export type PolygonDrawingInProgressSnapshot = boolean;

/**
 * 多边形绘制 Store 返回值接口。
 */
export interface PolygonSelectHookResult {
  /** 获取多边形绘制实时变化的 Observable 流。 */
  onPolygonParametersChanged: () => Observable<PolygonParametersSnapshot>;
  /** 获取多边形绘制进行中状态的 Observable 流。 */
  onPolygonDrawingInProgressChanged: () => Observable<PolygonDrawingInProgressSnapshot>;
  /** 显式开始监听多边形绘制参数事件。 */
  subscribe: () => void;
  /** 取消多边形绘制参数事件监听。 */
  unsubscribe: () => void;
}

//------ 多边形绘制状态 开始------
/**
 * 获取多边形绘制处理器派发的实时参数。
 */
export const useGetSelectPolygon = defineStore('get-select-polygon', (): PolygonSelectHookResult => {
  let subscriptions: Subscription[] = [];
  let hasSubscribed = false;
  const polygonParameters$ = new BehaviorSubject<PolygonParametersSnapshot>(null);
  const polygonDrawingInProgress$ = new BehaviorSubject<PolygonDrawingInProgressSnapshot>(false);
  const { getViewer } = useViewer();

  /**
   * 解析当前可用的草图绘制管理器。
   * @returns 草图绘制管理器；未就绪时返回 null。
   */
  function resolvePolygonDrawingParameters(): Observable<PolygonParametersSnapshot> | null {
    return getViewer()?.observePolygonDrawingParameters?.() as Observable<PolygonParametersSnapshot> | null;
  }

  /**
   * 解析多边形绘制事件流。
   * @returns 绘制事件流。
   */
  function resolvePolygonDrawingEvents(): Observable<PolygonDrawingEventType> | null {
    return getViewer()?.observePolygonDrawingEvent?.() as Observable<PolygonDrawingEventType> | null;
  }

  /**
   * 显式开始监听多边形绘制参数事件。
   */
  function subscribe(): void {
    const polygonDrawingParameters$ = resolvePolygonDrawingParameters();
    const polygonDrawingEvents$ = resolvePolygonDrawingEvents();
    if (!polygonDrawingParameters$ || !polygonDrawingEvents$ || hasSubscribed) {
      return;
    }
    subscriptions.push(
      polygonDrawingParameters$.subscribe((params: PolygonParametersSnapshot) => {
        polygonParameters$.next(params);
      }),
      polygonDrawingEvents$.subscribe((eventType: PolygonDrawingEventType) => {
        if (eventType === PolygonDrawingEventType.Start) {
          polygonDrawingInProgress$.next(true);
          return;
        }
        if (eventType === PolygonDrawingEventType.End) {
          polygonDrawingInProgress$.next(false);
          return;
        }
        throw new Error(`未知多边形绘制事件类型: ${String(eventType)}`);
      }),
    );
    hasSubscribed = true;
  }

  /**
   * 取消多边形绘制参数事件监听。
   */
  function unsubscribe(): void {
    subscriptions.forEach((subscription) => subscription.unsubscribe());
    subscriptions = [];
    hasSubscribed = false;
    polygonDrawingInProgress$.next(false);
  }

  return {
    onPolygonParametersChanged: (): Observable<PolygonParametersSnapshot> => polygonParameters$.asObservable(),
    onPolygonDrawingInProgressChanged: (): Observable<PolygonDrawingInProgressSnapshot> => polygonDrawingInProgress$.asObservable(),
    subscribe,
    unsubscribe,
  };
});
//------ 多边形绘制状态 结束------
